// Coaching Summary panel (Seller Skills page). An auxiliary, NON-SCORING signal, structured into three
// always-present sub-sections: SCORE ANALYSIS (the score explained + what worked / areas requiring
// improvement), TRENDS (the rep's per-skill trajectory, or a "building" note on a rep's first calls),
// and RECOMMENDATIONS (the code-computed decision-tree action items). The three sub-headers are always
// on screen — idle before a score, loading during, filled after. Quote-free; deep read is on Cross-Skill.

import { setText } from '../lib/escape.js';
import { displaySkillName, displaySkillNamesInText } from '../lib/skills.js';
import { navigateTo } from './sidenav.js';
import { wireCollapse } from '../lib/collapse.js';
import type { Coaching, CoachingPoint, CoachingTrend, CoachingTrendSkill, CoachingAction, UnscoredSkill, SkillBreakdownRow } from '../lib/types.js';

/** Display category for a skill that couldn't be scored. Authored labels; the detail beside them is the
 *  scorer's own reason. Keep in sync with the Message Center alert wording in main.ts. */
const UNSCORED_LABEL: Record<UnscoredSkill['state'], string> = {
  no_evidence: 'No evidence in the call',
  insufficient_transcript: 'Too little in the transcript to judge',
  not_applicable: "Didn't apply to this call",
};

// Direction shown as a word to the RIGHT of the scores (no leading arrow), coloured by direction.
const DIR_LABEL: Record<CoachingTrendSkill['direction'], string> = { improving: 'IMPROVING', declining: 'DECLINING', flat: 'STEADY' };

function subhead(title: string): HTMLElement {
  const h = document.createElement('div');
  h.className = 'coach-subhead';
  setText(h, title);
  return h;
}
/** A collapsible coaching section: a section subhead that toggles the body below it (persisted,
 *  default collapsed) — same header treatment as What worked / Areas requiring improvement. */
function appendCollapsible(root: HTMLElement, title: string, body: HTMLElement, key: string): void {
  const head = subhead(title);
  body.classList.add('coach-sec-body');
  root.append(head, body);
  wireCollapse(head, body, key);
}
function hint(text: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'coach-hint';
  setText(p, text);
  return p;
}

/** One why-led line: a claim followed by a muted "why". */
function pointRow(p: CoachingPoint): HTMLLIElement {
  const li = document.createElement('li');
  li.className = 'coach-point';
  const claim = document.createElement('span');
  claim.className = 'coach-claim';
  setText(claim, p.point.replace(/\.?\s*$/, '. '));
  const why = document.createElement('span');
  why.className = 'coach-why';
  setText(why, p.why);
  li.append(claim, why);
  return li;
}

/** An inner labelled list (What worked / Areas requiring improvement), with a colour dot. `gap` adds
 *  extra clearance above the block (used to separate "Areas requiring improvement" from what's above).
 *  Collapsible via its header (default collapsed, persisted under `key`). */
function pointList(title: string, iconClass: string, points: CoachingPoint[], key: string, gap = false): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'coach-block' + (gap ? ' coach-block-gap' : '');
  const head = document.createElement('div');
  head.className = 'coach-block-head';
  const ico = document.createElement('span');
  ico.className = `coach-ico ${iconClass}`;
  ico.setAttribute('aria-hidden', 'true');
  const label = document.createElement('span');
  label.className = 'coach-block-title';
  setText(label, title);
  head.append(ico, label);
  const list = document.createElement('ul');
  list.className = 'coach-list';
  for (const p of points) list.appendChild(pointRow(p));
  wrap.append(head, list);
  // Open by default. pointList builds only "What worked" and "Areas requiring improvement" — the two
  // sections that carry the actual coaching read, so they should be readable without a click. Trends,
  // Recent Trends and Where do we go from here? stay collapsed (wireCollapse's default).
  wireCollapse(head, list, key, false);
  return wrap;
}

/** A REQUIRED skill (minimum >= 1) with no evidence is a real miss, shown as "Missing" (a full gap) on
 *  the Performance Breakdown — NOT the same as a skill that genuinely couldn't be graded. Keep the two
 *  apart here so the Coaching Summary tells the same story as the breakdown + Message Center. */
function isMissing(u: UnscoredSkill): boolean {
  return u.state === 'no_evidence' && u.expected_score >= 1;
}

/** The exact "claim" line the Coaching Summary shows for a not-scored skill (Missing vs un-gradeable).
 *  Shared so the Performance Breakdown hover reads word-for-word the same as this page. */
function unscoredClaim(u: UnscoredSkill): string {
  return isMissing(u)
    ? `${displaySkillName(u.skill_name)} — Missing: no evidence in the call, counted as a full gap below your minimum.`
    : `${displaySkillName(u.skill_name)} — ${UNSCORED_LABEL[u.state]}.`;
}

/** The per-skill coaching detail for a Performance Breakdown pill hover — the SAME text the Coaching
 *  Summary carries. A not-scored skill reproduces the Coaching "Marked Missing / couldn't score" line
 *  verbatim (claim + the scorer's reason); a scored skill gets the pill's status word + the model's
 *  per-skill summary. Returns null when there's nothing to say (e.g. still awaiting a run). */
export type CoachTone = 'neg' | 'amber' | 'pos' | 'zero' | 'mute';
export interface SkillCoachingDetail { label: string; why: string; tone: CoachTone; }

export function skillCoachingDetail(
  skillId: string,
  breakdown: SkillBreakdownRow[],
  unscored: UnscoredSkill[],
): SkillCoachingDetail | null {
  const u = unscored.find((x) => x.skill_id === skillId);
  if (u) {
    return {
      label: unscoredClaim(u),
      why: displaySkillNamesInText(u.reason) || 'The scorer found nothing in this call to assess for this skill.',
      tone: isMissing(u) ? 'neg' : 'mute',
    };
  }
  const s = breakdown.find((r) => r.skill_id === skillId);
  if (s && s.evidence_state === 'scored' && s.actual_score != null) {
    const gap = Math.round(s.actual_score - s.expected_score);
    // Same status word the pill shows (performanceTable.paintRow).
    const word = gap < 0 ? (gap <= -2 ? 'Below' : 'Watch') : gap > 0 ? 'Above' : 'On target';
    const tone: CoachTone = gap < 0 ? (gap <= -2 ? 'neg' : 'amber') : gap > 0 ? 'pos' : 'zero';
    const scoreInfo = ` (scored ${s.actual_score} of ${s.expected_score || 'opt'})`;
    return {
      label: `${displaySkillName(s.skill_name)} — ${word}${scoreInfo}:`,
      why: displaySkillNamesInText(s.summary ?? '') || 'No further detail was recorded for this skill on this call.',
      tone,
    };
  }
  return null;
}

/** One "no numeric score" group: a titled block, one row per skill (name + category, then the scorer's
 *  own reason). `missing` frames the rows as penalised misses rather than un-gradeable blanks. */
function unscoredGroup(title: string, items: UnscoredSkill[], key: string): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'coach-block coach-block-gap';
  const head = document.createElement('div');
  head.className = 'coach-block-head';
  const ico = document.createElement('span');
  ico.className = 'coach-ico coach-ico-warn';
  ico.setAttribute('aria-hidden', 'true');
  const label = document.createElement('span');
  label.className = 'coach-block-title';
  setText(label, title);
  head.append(ico, label);
  const listEl = document.createElement('ul');
  listEl.className = 'coach-list';
  for (const u of items) {
    const li = document.createElement('li');
    li.className = 'coach-point';
    const claim = document.createElement('span');
    claim.className = 'coach-claim';
    setText(claim, unscoredClaim(u) + ' ');
    const why = document.createElement('span');
    why.className = 'coach-why';
    setText(why, displaySkillNamesInText(u.reason) || 'The scorer found nothing in this call to assess for this skill.');
    li.append(claim, why);
    listEl.appendChild(li);
  }
  wrap.append(head, listEl);
  wireCollapse(head, listEl, key, false); // open by default — it explains the blank gaps
  return wrap;
}

/** Not-scored skills, split so a required "Missing" miss reads as a penalty and the genuinely un-gradeable
 *  ones stay neutral. Returns one or two blocks. Same chrome as What worked / Areas requiring improvement. */
function unscoredBlock(list: UnscoredSkill[]): DocumentFragment {
  const frag = document.createDocumentFragment();
  const missing = list.filter(isMissing);
  const ungraded = list.filter((u) => !isMissing(u));
  if (missing.length) frag.appendChild(unscoredGroup('Marked Missing', missing, 'coach-unscored-missing'));
  if (ungraded.length) frag.appendChild(unscoredGroup("Skills we couldn't score", ungraded, 'coach-unscored'));
  return frag;
}

/** One trend row: skill name, then the move (arrow + first→latest) pulled close to the name. */
function trendRow(s: CoachingTrendSkill): HTMLLIElement {
  const li = document.createElement('li');
  li.className = `coach-trend-row coach-dir-${s.direction}`;
  const name = document.createElement('span');
  name.className = 'coach-trend-name';
  setText(name, displaySkillName(s.skill_name));
  const move = document.createElement('span');
  move.className = 'coach-trend-move';
  const from = s.first == null ? '?' : String(s.first);
  const to = s.latest == null ? '?' : String(s.latest);
  setText(move, `${from} → ${to}`);
  const label = document.createElement('span');
  label.className = 'coach-trend-label';
  setText(label, DIR_LABEL[s.direction]);
  li.append(name, move, label);
  return li;
}

/** A light label separating a group of trend rows (only used in top-performer mode). */
function trendGroupLabel(text: string): HTMLElement {
  const d = document.createElement('div');
  d.className = 'coach-trend-group';
  setText(d, text);
  return d;
}
function trendList(skills: CoachingTrendSkill[]): HTMLElement {
  const rows = document.createElement('ul');
  rows.className = 'coach-trend-list';
  for (const s of skills) rows.appendChild(trendRow(s));
  return rows;
}

function trendRows(t: CoachingTrend): HTMLElement {
  const wrap = document.createElement('div');
  wrap.appendChild(hint(`This rep's last ${t.calls} calls`));
  const all = [...t.improving, ...t.stalled];
  if (t.top_performer) {
    // Split the story the CRO cares about: what is consistently at/above target (the formula to
    // replicate) vs the handful still below it (where a lift would compound an already-strong rep).
    const above = all.filter((s) => s.above_expected);
    const below = all.filter((s) => !s.above_expected);
    if (above.length) {
      wrap.appendChild(trendGroupLabel('Consistently at or above target, worth replicating'));
      wrap.appendChild(trendList(above));
    }
    if (below.length) {
      wrap.appendChild(trendGroupLabel('Below target, where a lift would compound'));
      wrap.appendChild(trendList(below));
    }
  } else {
    wrap.appendChild(trendList(all));
  }
  return wrap;
}

/** Top-performer opening: a deterministic, code-computed callout so the summary LEADS with recognition,
 *  independent of the model prose. */
function topPerformerBanner(t: CoachingTrend): HTMLElement {
  const div = document.createElement('div');
  div.className = 'coach-topperf' + (t.confirmed ? ' coach-topperf-confirmed' : '');
  const h = document.createElement('span');
  h.className = 'coach-topperf-h';
  setText(h, t.confirmed ? 'Top performer' : 'Consistently strong');
  const p = document.createElement('span');
  p.className = 'coach-topperf-txt';
  // Two tiers: >=100 (met/exceeded expectation) is stated confidently; 90–99 stays a measured
  // "possibly a top performer, keep a close eye" since the score scale calls that "close, but not enough".
  setText(
    p,
    t.confirmed
      ? `This rep has met or exceeded expectation (${t.sustained_floor} or above) across their last ${t.calls} calls. ` +
          `This is a top performer. Study what they do and look at replicating it across the team.`
      : `This rep has scored above ${t.sustained_floor} across their last ${t.calls} calls, holding at or above target. ` +
          `Possibly a top performer, keep a close eye on this one, it may be worth replicating what they do.`,
  );
  div.append(h, p);
  return div;
}

/** An action item. Cohort-Diagnosis actions get a button that deep-links to that page. */
function actionRow(a: CoachingAction): HTMLLIElement {
  const li = document.createElement('li');
  li.className = 'coach-action';
  const text = document.createElement('span');
  setText(text, displaySkillNamesInText(a.text));
  li.appendChild(text);
  // The Cohort Diagnostic deep-link button is removed (CRO Zone is hidden from customers). Other nav
  // actions keep their "Open" button.
  if (a.nav && a.kind !== 'cohort-diagnosis') {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'coach-action-btn';
    setText(btn, 'Open');
    const target = a.nav;
    btn.addEventListener('click', () => navigateTo(target));
    li.appendChild(btn);
  }
  return li;
}

const TREND_BUILDING = 'Trends kick in once you have scored more calls for this rep. Score them again to build the trajectory.';

/** Render the loaded coaching summary into `root` (clears it first). No-op if root is null. */
export function renderCoaching(root: HTMLElement | null, nextRoot: HTMLElement | null, c: Coaching): void {
  if (!root) return;
  root.innerHTML = '';
  root.classList.remove('coach-loading');
  // "Where do we go from here?" renders into nextRoot (which sits BELOW the relocated Recent Trends).
  if (nextRoot) { nextRoot.innerHTML = ''; nextRoot.classList.remove('coach-loading'); }

  const topPerformer = c.trend?.top_performer === true;

  // ---- Score analysis (header removed per feedback; the summary leads straight into the content) ----
  // A proven top performer LEADS with recognition (a deterministic banner), then the model's tldr.
  if (topPerformer && c.trend) root.appendChild(topPerformerBanner(c.trend));
  if (c.tldr) {
    const tldr = document.createElement('p');
    tldr.className = 'coach-tldr';
    setText(tldr, c.tldr);
    root.appendChild(tldr);
  }
  if (c.worked.length) {
    root.appendChild(pointList(topPerformer ? "What's working, worth replicating" : 'What worked', 'coach-ico-ok', c.worked, 'coach-worked'));
  }
  if (c.improve.length) {
    // On a top performer these are refinements that would compound an already-strong rep, not deficits.
    root.appendChild(
      pointList(topPerformer ? 'Where a lift would compound' : 'Areas requiring improvement', 'coach-ico-warn', c.improve, 'coach-improve', true),
    );
  }
  // Skills that couldn't be scored — explains every blank gap the manager sees on the score breakdown.
  if (c.unscored && c.unscored.length) root.appendChild(unscoredBlock(c.unscored));

  const haveTrend = !!(c.trend && (c.trend.improving.length || c.trend.stalled.length));

  // ---- Trends (collapsible) — the rep's per-skill trajectory table ----
  const trendsBody = document.createElement('div');
  if (haveTrend && c.trend) trendsBody.appendChild(trendRows(c.trend));
  else if (c.trend_pending) trendsBody.appendChild(hint(TREND_BUILDING));
  else trendsBody.appendChild(hint('Trend history is not available for this call.'));
  appendCollapsible(root, 'Trends', trendsBody, 'coach-trends');

  // ---- Where do we go from here? (collapsible, now a peer section with a matching header) ----
  const nextBody = document.createElement('div');
  if (haveTrend && c.trend && c.trend.actions.length) {
    const list = document.createElement('ul');
    list.className = 'coach-actions';
    for (const a of c.trend.actions) list.appendChild(actionRow(a));
    nextBody.appendChild(list);
  } else if (haveTrend) {
    nextBody.appendChild(hint('No corrective actions right now, the trajectory looks healthy.'));
  } else {
    nextBody.appendChild(hint('Recommendations appear once you have scored a few calls for this rep.'));
  }
  appendCollapsible(nextRoot ?? root, 'Where do we go from here?', nextBody, 'coach-next');
}

/** The always-present scaffold: three sub-headers with a per-section hint. Used before a call is scored
 *  and after Clear, so the structure is on screen from the moment the page is up. */
function scaffold(root: HTMLElement, nextRoot: HTMLElement | null, scoreHint: string, loading: boolean): void {
  root.innerHTML = '';
  root.classList.toggle('coach-loading', loading);
  if (nextRoot) { nextRoot.innerHTML = ''; nextRoot.classList.toggle('coach-loading', loading); }
  root.appendChild(hint(scoreHint)); // Score-analysis lead-in (its "Score Analysis" header was removed)
  root.appendChild(subhead('Trends'));
  root.appendChild(hint('Trends, and where to go from here, appear once you have scored a few calls for the same rep.'));
}

/** Idle state (no scored call / after Clear). */
export function renderCoachingIdle(root: HTMLElement | null, nextRoot: HTMLElement | null = null): void {
  if (root) scaffold(root, nextRoot, 'Score a call to see what worked and where it fell short.', false);
}

/** Loading state — the first coaching for a call is a live model call (~seconds). */
export function coachingLoading(root: HTMLElement | null, nextRoot: HTMLElement | null = null): void {
  if (root) scaffold(root, nextRoot, 'Reading the call and writing the coaching summary…', true);
}

/** Empty the panel (used on import / clear / stop / reset) back to the idle scaffold. */
export function clearCoaching(root: HTMLElement | null, nextRoot: HTMLElement | null = null): void {
  renderCoachingIdle(root, nextRoot);
}
